import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { LifeBuoy, Send, Phone, PhoneOff } from "lucide-react";
import { useTwilioVoice } from "@/context/TwilioVoiceContext";

/**
 * Super Admin support console.
 * Left column: all threads sorted by last-message time.
 * Right column: full conversation with the selected user + reply box + call button.
 */
export default function AdminSupportConsole({ L }) {
  const [threads, setThreads] = useState([]);
  const [activeUserId, setActiveUserId] = useState(null);
  const [activeThread, setActiveThread] = useState(null); // meta (user_name etc)
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const tv = useTwilioVoice();

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/admin/support/threads");
      setThreads(data || []);
      // If nothing selected yet, auto-select the top thread
      if (!activeUserId && data && data.length) {
        setActiveUserId(data[0].user_id);
        setActiveThread(data[0]);
      }
    } catch { /* ignore */ }
  };

  const loadMessages = async () => {
    if (!activeUserId) return;
    try {
      const { data } = await api.get(`/admin/support/threads/${activeUserId}/messages`);
      setMessages(data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadThreads();
    const id = setInterval(loadThreads, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeUserId) return;
    loadMessages();
    const id = setInterval(loadMessages, 6000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUserId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, activeUserId]);

  const send = async () => {
    const b = body.trim();
    if (!b || !activeUserId || sending) return;
    setSending(true);
    try {
      await api.post(`/admin/support/threads/${activeUserId}/messages`, { body: b });
      setBody("");
      await loadMessages();
      await loadThreads();
    } catch { /* silent */ }
    finally { setSending(false); }
  };

  const pickThread = (t) => {
    setActiveUserId(t.user_id);
    setActiveThread(t);
    setMessages([]);
  };

  const callUser = () => {
    if (!activeUserId || !tv.enabled) return;
    tv.startCall(`user:${activeUserId}`);
  };

  const fmt = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(L ? "el-GR" : "en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  const fmtShort = (iso) => {
    if (!iso) return "";
    const now = new Date();
    const d = new Date(iso);
    const diffMin = Math.round((now - d) / 60000);
    if (diffMin < 1) return L ? "τώρα" : "now";
    if (diffMin < 60) return `${diffMin}${L ? "λ" : "m"}`;
    if (diffMin < 1440) return `${Math.round(diffMin / 60)}${L ? "ω" : "h"}`;
    return d.toLocaleDateString(L ? "el-GR" : "en-GB", { day: "2-digit", month: "short" });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="admin-support-console">
      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] h-[640px]">
        {/* Threads list */}
        <div className="border-r border-slate-200 flex flex-col bg-slate-50/40">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <LifeBuoy className="w-4 h-4 text-slate-500" />
            <div className="text-sm font-heading font-bold text-slate-900">{L ? "Συνομιλίες" : "Threads"}</div>
            <span className="ml-auto text-xs text-slate-500 font-semibold">{threads.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {threads.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">
                {L ? "Καμία συνομιλία ακόμη" : "No conversations yet"}
              </div>
            )}
            {threads.map((t) => {
              const active = t.user_id === activeUserId;
              return (
                <button
                  key={t.user_id}
                  onClick={() => pickThread(t)}
                  data-testid={`admin-support-thread-${t.user_id}`}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-white ${active ? "bg-white" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-900 truncate">{t.user_name || t.user_email}</span>
                        {t.unread > 0 && (
                          <span className="min-w-[18px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{t.unread}</span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mt-0.5">
                        {t.user_role?.replace("_", " ")} {t.company_name ? `· ${t.company_name}` : ""}
                      </div>
                      <div className="text-xs text-slate-600 mt-1 truncate">
                        {t.last_from === "admin" && <span className="text-slate-400">↳ </span>}
                        {t.last_body}
                      </div>
                    </div>
                    <span className="text-[10px] text-slate-400 whitespace-nowrap">{fmtShort(t.last_at)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Conversation */}
        <div className="flex flex-col">
          {!activeUserId ? (
            <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
              {L ? "Επιλέξτε συνομιλία από αριστερά" : "Pick a conversation on the left"}
            </div>
          ) : (
            <>
              <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-heading font-bold text-slate-900 truncate">{activeThread?.user_name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {activeThread?.user_email} · {activeThread?.user_role?.replace("_", " ")}
                    {activeThread?.company_name ? ` · ${activeThread.company_name}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {tv.enabled && (
                    <button
                      data-testid="admin-support-call"
                      onClick={callUser}
                      disabled={!tv.deviceReady || tv.outOfMinutes || tv.status !== "idle" || !activeThread?.user_has_phone}
                      title={activeThread?.user_has_phone ? (L ? "Κλήση χρήστη" : "Call user") : (L ? "Ο χρήστης δεν έχει τηλέφωνο" : "User has no phone")}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                    >
                      {tv.status === "in-call" ? <PhoneOff className="w-3.5 h-3.5" /> : <Phone className="w-3.5 h-3.5" />}
                      {tv.status === "in-call" ? (L ? "Σε κλήση" : "In call") : (L ? "Κλήση" : "Call")}
                    </button>
                  )}
                </div>
              </header>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50/50" data-testid="admin-support-messages">
                {messages.map((m) => {
                  const mine = m.from_role === "admin";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] px-3.5 py-2 rounded-lg text-sm ${mine ? "bg-[#0052CC] text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                        <div className="whitespace-pre-wrap break-words">{m.body}</div>
                        <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-slate-400"}`}>{fmt(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-slate-200 p-3 flex items-end gap-2 bg-white">
                <textarea
                  data-testid="admin-support-input"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  rows={2}
                  placeholder={L ? "Απάντηση προς τον χρήστη..." : "Reply to user..."}
                  className="flex-1 resize-none px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/30"
                />
                <button
                  data-testid="admin-support-send"
                  onClick={send}
                  disabled={!body.trim() || sending}
                  className="w-10 h-10 rounded-md bg-[#0052CC] hover:bg-[#0043A8] text-white flex items-center justify-center shadow-sm disabled:opacity-40"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
